import type { AxiosProgressEvent } from 'axios'

import { axiosInstance, resolveUrl } from '@/lib/api-client'

/**
 * Image uploads go to the rootpoint-media Worker, which writes to R2 and serves
 * from cdn.rootpoint.io. One call in, one public URL out, stored as-is.
 *
 * Auth is the caller's normal POS session: `axiosInstance` already attaches the
 * bearer token, and the Worker validates it against the platform API.
 */

/** Public base for everything the media Worker stores. */
export const MEDIA_PUBLIC_BASE = 'https://cdn.rootpoint.io'

/** Resolved to the Worker URL by `api-client`'s `resolveUrl` — the host lives there.
    `axiosInstance` is used (not `api.post`) because it carries the session header,
    and it does NOT resolve paths itself, so the resolve is explicit below. */
const MEDIA_UPLOAD_PATH = '/api/media/upload'

export interface UploadImageOptions {
  /** Key prefix under the org, e.g. `products`, `logos`. Defaults to `uploads`. */
  folder?: string
  signal?: AbortSignal
  /** 0–100 as the browser sends the body. Not reported for remote-URL sources. */
  onProgress?: (percent: number) => void
}

export interface UploadedImage {
  url: string
  key: string
  size: number
  contentType: string
}

/** True for a URL already on our CDN, which must not be re-uploaded. */
export function isHostedByUs(url: string): boolean {
  return url.startsWith(`${MEDIA_PUBLIC_BASE}/`)
}

/**
 * Upload a `File`, or re-host a remote `http(s)` image. Remote sources are
 * fetched by the Worker, not the browser — a browser fetch depends on the third
 * party's CORS headers and failed intermittently.
 */
export async function uploadImage(source: File | string, options: UploadImageOptions = {}): Promise<string> {
  const form = new FormData()
  if (typeof source === 'string') {
    form.append('url', source)
  } else {
    form.append('file', source)
  }
  form.append('folder', options.folder ?? 'uploads')

  const response = await axiosInstance.post<UploadedImage>(resolveUrl(MEDIA_UPLOAD_PATH), form, {
    // Cross-origin call to the Worker. `axiosInstance` defaults to credentials
    // mode for the API's cookie session, which makes the browser demand an
    // Allow-Credentials preflight the Worker deliberately does not grant. The
    // bearer header still goes — withCredentials only governs cookies.
    withCredentials: false,
    signal: options.signal,
    onUploadProgress: options.onProgress
      ? (event: AxiosProgressEvent) => {
          const total = event.total ?? (typeof source === 'string' ? 0 : source.size)
          if (total > 0) options.onProgress?.(Math.round((event.loaded * 100) / total))
        }
      : undefined,
  })
  return response.data.url
}

function dataUriToFile(dataUri: string, filename: string): File {
  const [header, base64] = dataUri.split(',')
  const mime = header.match(/:(.*?);/)?.[1] ?? 'image/png'
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new File([bytes], filename, { type: mime })
}

/** Upload a `data:` URI (canvas previews). Anything else is returned untouched. */
export async function uploadDataUriImage(
  dataUri: string,
  filename = 'preview.png',
  options: UploadImageOptions = {},
): Promise<string> {
  if (!dataUri.startsWith('data:')) return dataUri
  return uploadImage(dataUriToFile(dataUri, filename), options)
}
